/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */

/**
 * NOTIFICATION QUEUE PANEL
 *
 * What the Zapier BCC used to give you: every lead alert, who it went to, and
 * what happened to it. Per-agent SendGrid sends took that away and this is
 * where it moved.
 *
 * Two things in one list:
 *   STAGED — pending waves whose scheduledFor hasn't arrived. A tier-0 wave
 *            sits here 30 minutes before it fires.
 *   SENT   — history, newest first.
 *
 * Expanding a row loads GET /admin/priority/leads/:id, which returns every
 * recipient with their individual delivery state. That's the actual BCC
 * replacement — the list view only tells you a wave went out.
 *
 * The OVERDUE count in the header is the one number that should never be
 * non-zero: pending waves more than 5 minutes past due mean the cron sweep in
 * jobs/leadNotificationScheduler has stopped running.
 */

import React, { useState, useEffect, useCallback } from 'react';
import AuthService from '../../services/authService';

const API_URL = process.env.REACT_APP_API_URL || 'https://back-end-x7tx.onrender.com/api';

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Staged' },
  { key: 'sent', label: 'Sent' },
  { key: 'failed', label: 'Failed' },
  { key: 'skipped', label: 'Skipped' },
];

const TIER_LABEL = { 3: 'Tier 3 · T+0', 2: 'Tier 2 · T+10', 1: 'Tier 1 · T+20', 0: 'Tier 0 · T+30' };

// Most engaged state wins, so one glance down the column tells you who acted.
const EMAIL_STATUS_STYLE = {
  clicked:   { bg: 'rgba(22,163,74,0.10)',  fg: '#15803d', label: 'Clicked' },
  opened:    { bg: 'rgba(37,99,235,0.08)',  fg: '#1d4ed8', label: 'Opened' },
  delivered: { bg: '#f3f4f6',               fg: '#4b5563', label: 'Delivered' },
  processed: { bg: '#f3f4f6',               fg: '#9ca3af', label: 'Sent' },
  bounced:   { bg: 'rgba(220,38,38,0.08)',  fg: '#b91c1c', label: 'Bounced' },
  dropped:   { bg: 'rgba(220,38,38,0.08)',  fg: '#b91c1c', label: 'Dropped' },
  spam:      { bg: 'rgba(220,38,38,0.08)',  fg: '#b91c1c', label: 'Spam report' },
  unknown:   { bg: '#f9fafb',               fg: '#9ca3af', label: '—' },
};

const fmtTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
  });
};

const NotificationQueuePanel = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('all');
  const [days, setDays] = useState(7);

  const [openRow, setOpenRow] = useState(null);
  const [detail, setDetail] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/admin/priority/queue?status=${status}&days=${days}&limit=50`,
        { headers: { Authorization: `Bearer ${AuthService.getToken()}` } }
      );
      const json = await res.json();
      if (json.success) {
        setRows(json.data || []);
        setSummary(json.summary || null);
      } else {
        setError(json.message || 'Failed to load queue');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [status, days]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  // Staged waves have a live countdown, so a stale page is misleading here in a
  // way it isn't on the other admin panels.
  useEffect(() => {
    const t = setInterval(fetchQueue, 60000);
    return () => clearInterval(t);
  }, [fetchQueue]);

  const openDetail = async (row) => {
    if (openRow === row.id) { setOpenRow(null); return; }
    setOpenRow(row.id);
    if (!row.lead?.id || detail[row.id]) return;

    setDetailLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/priority/leads/${row.lead.id}`, {
        headers: { Authorization: `Bearer ${AuthService.getToken()}` },
      });
      const json = await res.json();
      if (json.success) {
        // The endpoint returns all four waves for the User; keep the one
        // matching this row's tier.
        const wave = (json.data.waves || []).find(w => w.tier === row.tier);
        setDetail(prev => ({ ...prev, [row.id]: wave || { recipients: [] } }));
      }
    } catch (err) {
      console.error('Error loading wave detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div>
      <div className="dashboard-card">
        <div className="card-header"><h3>Notification Queue</h3></div>
        <p className="card-note">
          Every lead alert, staged and sent. Expand a row to see each agent who
          received it and what happened to their copy — the visibility the
          shared BCC used to provide.
        </p>

        {/* Summary */}
        {summary && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '12px',
            marginBottom: '20px'
          }}>
            {[
              { label: 'Staged', value: summary.pending, color: '#2563eb' },
              { label: `Sent (${summary.windowDays}d)`, value: summary.sent, color: '#111' },
              { label: 'Failed', value: summary.failed, color: summary.failed > 0 ? '#dc2626' : '#111' },
              { label: 'Skipped', value: summary.skipped, color: '#9ca3af' },
            ].map(s => (
              <div key={s.label} style={{
                padding: '14px', background: '#f9fafb',
                border: '1px solid #e5e7eb', borderRadius: '10px'
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: s.color }}>{s.value}</div>
                <div className="stats-label">{s.label}</div>
              </div>
            ))}

            {/* Should always be zero. Non-zero = the cron sweep isn't running,
                and every lead alert is silently stuck. */}
            <div style={{
              padding: '14px',
              background: summary.overdue > 0 ? 'rgba(220,38,38,0.06)' : '#f9fafb',
              border: `1px solid ${summary.overdue > 0 ? 'rgba(220,38,38,0.25)' : '#e5e7eb'}`,
              borderRadius: '10px'
            }}>
              <div style={{
                fontSize: '1.5rem', fontWeight: 700,
                color: summary.overdue > 0 ? '#dc2626' : '#111'
              }}>
                {summary.overdue}
              </div>
              <div className="stats-label">Overdue</div>
              {summary.overdue > 0 && (
                <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px' }}>
                  scheduler may be down
                </div>
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {STATUS_FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setStatus(f.key)}
              style={{
                padding: '5px 12px', fontSize: '12px', fontWeight: 600,
                borderRadius: '999px', cursor: 'pointer',
                border: `1px solid ${status === f.key ? '#111' : '#e5e7eb'}`,
                background: status === f.key ? '#111' : '#fff',
                color: status === f.key ? '#fff' : '#6b7280',
              }}
            >
              {f.label}
            </button>
          ))}

          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            style={{
              marginLeft: 'auto', padding: '5px 10px', fontSize: '12px',
              borderRadius: '8px', border: '1px solid #e5e7eb', color: '#6b7280'
            }}
          >
            <option value={1}>Last 24h</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
          </select>
        </div>

        {error && <div className="verdict warn">{error}</div>}
        {loading && <div style={{ fontSize: '13px', color: '#9ca3af' }}>Loading…</div>}
        {!loading && rows.length === 0 && (
          <div style={{ fontSize: '13px', color: '#9ca3af', padding: '20px 0' }}>
            No notification waves in this window.
          </div>
        )}

        {/* Rows */}
        {rows.map(row => (
          <div key={row.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
            <div
              onClick={() => openDetail(row)}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '12px 4px', cursor: 'pointer'
              }}
            >
              {/* Status pill */}
              <div style={{
                minWidth: '78px', textAlign: 'center',
                padding: '4px 8px', borderRadius: '999px',
                fontSize: '11px', fontWeight: 600,
                background: row.isOverdue ? 'rgba(220,38,38,0.08)'
                  : row.isStaged ? 'rgba(37,99,235,0.08)'
                  : row.status === 'failed' ? 'rgba(220,38,38,0.08)'
                  : row.status === 'skipped' ? '#f9fafb'
                  : '#f3f4f6',
                color: row.isOverdue ? '#b91c1c'
                  : row.isStaged ? '#1d4ed8'
                  : row.status === 'failed' ? '#b91c1c'
                  : row.status === 'skipped' ? '#9ca3af'
                  : '#4b5563',
              }}>
                {row.isOverdue ? 'Overdue'
                  : row.isStaged ? `in ${row.minutesUntilSend}m`
                  : row.status === 'sent' ? 'Sent'
                  : row.status}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#111' }}>
                  {row.lead?.name || 'Unknown user'}
                  <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: '8px' }}>
                    {row.leadTier} · {row.pointsCost} pts
                  </span>
                  {row.lead?.isClaimed && (
                    <span style={{
                      marginLeft: '8px', fontSize: '11px', padding: '2px 6px',
                      borderRadius: '999px', background: '#f3f4f6', color: '#6b7280'
                    }}>
                      claimed
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                  {TIER_LABEL[row.tier]} · {row.pool?.name || '—'} · {row.recipientCount} recipient{row.recipientCount === 1 ? '' : 's'}
                </div>
              </div>

              <div style={{ textAlign: 'right', fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                <div>{row.sentAt ? fmtTime(row.sentAt) : fmtTime(row.scheduledFor)}</div>
                {/* Which pipe actually carried it. Useful while both are live. */}
                {row.sentVia && (
                  <div style={{ fontSize: '11px', color: row.sentVia === 'sendgrid' ? '#15803d' : '#d97706' }}>
                    via {row.sentVia}
                  </div>
                )}
              </div>
            </div>

            {row.lastError && (
              <div style={{
                fontSize: '12px', color: '#b91c1c', padding: '0 4px 10px 90px'
              }}>
                {row.lastError} · {row.attempts} attempt{row.attempts === 1 ? '' : 's'}
              </div>
            )}
            {row.skipReason && (
              <div style={{ fontSize: '12px', color: '#9ca3af', padding: '0 4px 10px 90px' }}>
                {row.skipReason}
              </div>
            )}

            {/* Per-recipient detail */}
            {openRow === row.id && (
              <div style={{
                margin: '0 0 14px 90px', padding: '12px 14px',
                background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px'
              }}>
                {detailLoading && !detail[row.id] && (
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>Loading recipients…</div>
                )}

                {detail[row.id]?.recipients?.length === 0 && (
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                    No recipients on this wave.
                  </div>
                )}

                {detail[row.id]?.recipients?.map(r => {
                  const st = EMAIL_STATUS_STYLE[r.email_status?.status || 'unknown'];
                  return (
                    <div key={r._id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '7px 0', borderBottom: '1px solid #f0f0f0', fontSize: '13px'
                    }}>
                      <div style={{ minWidth: 0 }}>
                        <span style={{ fontWeight: 600, color: '#111' }}>{r.name}</span>
                        <span style={{ color: '#9ca3af', marginLeft: '8px', fontSize: '12px' }}>
                          {r.email}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', whiteSpace: 'nowrap' }}>
                        {r.email_status?.at && (
                          <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                            {fmtTime(r.email_status.at)}
                          </span>
                        )}
                        <span style={{
                          padding: '3px 9px', borderRadius: '999px',
                          fontSize: '11px', fontWeight: 600,
                          background: st.bg, color: st.fg
                        }}>
                          {st.label}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {/* Waves that predate the event webhook have no tracking at all.
                    Say so, rather than showing a column of dashes that reads as
                    "nobody opened it". */}
                {detail[row.id]?.recipients?.length > 0 &&
                  detail[row.id].recipients.every(r => (r.email_status?.status || 'unknown') === 'unknown') && (
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
                    No delivery tracking for this wave — sent before the SendGrid
                    event webhook was enabled, or sent via Zapier.
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default NotificationQueuePanel;